import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SendIcon from "@material-ui/icons/Send";
import StarIcon from "@material-ui/icons/Star";
import MessageTemplateApi, {
  AUDIENCE_LABELS,
  MessageAudience,
  MessageTemplateRes,
} from "api/MessageTemplateApi";
import OrderMessageApi, { SendMethod } from "api/OrderMessageApi";

/** visit Phase 3: 短縮 URL の想定長さ (https://t.no-tel.com/v1/s/<12 chars> = 38 chars) */
const SHORT_URL_LENGTH = 38;
/** URL 短縮 ON 時の SMS 本文 目安上限 (置換後、 URL 含む) */
const URL_SMS_SOFT_LIMIT = 75;
/** SMS 1 通の文字数上限 */
const SMS_CHARS_PER_SEGMENT = 70;
/** SMS 本文の URL_PAGE プレースホルダ */
const URL_PAGE_PLACEHOLDER = "{URL_PAGE}";

/** SMS 想定長さ計算 (URL 短縮 ON 時は短縮 URL 長で置換後 length) */
function calcSmsLength(body: string, hasUrlPageContent: boolean): number {
  if (!hasUrlPageContent || !body.includes(URL_PAGE_PLACEHOLDER)) {
    return body.length;
  }
  // {URL_PAGE} → 短縮 URL 38 chars 置換後で算出
  return body.split(URL_PAGE_PLACEHOLDER).join("X".repeat(SHORT_URL_LENGTH))
    .length;
}

interface Props {
  orderId: number | null;
  formData?: { orderId?: number | null };
  // #477: 配車画面 (DriverSchedule) から Dialog で開く際の初期タブ/送信先指定
  initialTab?: MessageAudience;
  initialDriverTarget?: "outward" | "return" | "both";
  // #477: 統合 Dialog (DriverDispatchSendDialog) から外部 footer の「保存して送信」 ボタンで
  // send を trigger する場合、 内部の「今送信」 ボタンを隠す
  hideSendButton?: boolean;
  // #477: 送信成功時に parent (Dialog 等) で order を refetch するための callback (赤色判定の即時反映)
  onSent?: () => void;
  // #477: 本文 TextField の rows を上書き (Dialog 内で送信方法 RadioGroup がファーストビューに入るよう)
  compactRows?: number;
  // 新規受注 (orderId が null) で「送信」 を押されたとき、 parent 側で先に保存して
  // 採番された orderId を返してもらうための callback。 未指定なら従来通り alert で抜ける。
  onRequestSaveBeforeSend?: () => Promise<number | null>;
}

// #477: 外部 (DriverDispatchSendDialog 等) から send を imperative に呼ぶための handle
export interface OrderMessagePanelHandle {
  send: () => Promise<void>;
}

// #473: 業態で表示タブを出し分け。 delivery は staff の代わりに driver。
const VISIT_AUDIENCE_TABS: MessageAudience[] = ["customer", "cast", "staff"];
const DELIVERY_AUDIENCE_TABS: MessageAudience[] = ["customer", "cast", "driver"];

const METHODS_BY_AUDIENCE: Record<
  MessageAudience,
  { value: SendMethod; label: string }[]
> = {
  customer: [
    { value: "sms", label: "SMS" },
    { value: "mail", label: "メール" },
  ],
  cast: [
    { value: "mail", label: "メール" },
    { value: "line", label: "LINE" },
  ],
  staff: [{ value: "line", label: "LINE グループ (タステック公式)" }],
  // #473 (5/29): driver は LINE (会社公式) / メール (SMS は廃止)
  driver: [
    { value: "line", label: "LINE" },
    { value: "mail", label: "メール" },
  ],
};

const OrderMessagePanel = forwardRef<OrderMessagePanelHandle, Props>(
  (
    {
      orderId,
      formData,
      initialTab,
      initialDriverTarget,
      hideSendButton,
      onSent,
      compactRows,
      onRequestSaveBeforeSend,
    },
    ref
  ) => {
  const companyId = useSelector(
    (state: { account: { staff: { companyId: number } } }) =>
      state.account.staff.companyId
  );
  // #473: 業態で表示タブを出し分け (delivery=ドライバー向け / visit=スタッフ向け)
  const businessType = useSelector(
    (state: {
      account: {
        staff: { company?: { companyGroup?: { businessType?: string } } };
      };
    }) => state.account.staff.company?.companyGroup?.businessType
  );
  const AUDIENCE_TABS =
    businessType === "delivery" ? DELIVERY_AUDIENCE_TABS : VISIT_AUDIENCE_TABS;
  const effectiveOrderId = orderId ?? formData?.orderId ?? null;

  const [tab, setTab] = useState<MessageAudience>(initialTab ?? "customer");
  // #473: audience=driver のときの送信先 (送り/迎え/両方)
  const [driverTarget, setDriverTarget] = useState<
    "outward" | "return" | "both"
  >(initialDriverTarget ?? "outward");
  const [templates, setTemplates] = useState<MessageTemplateRes[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<
    Record<MessageAudience, number | null>
  >({ customer: null, cast: null, staff: null, driver: null });
  const [bodies, setBodies] = useState<Record<MessageAudience, string>>({
    customer: "",
    cast: "",
    staff: "",
    driver: "",
  });
  const [subjects, setSubjects] = useState<Record<MessageAudience, string>>({
    customer: "",
    cast: "",
    staff: "",
    driver: "",
  });
  // visit Phase 3: URL 先ページ本文 + タイトル (テンプレに urlPageBody が設定されてる時のみ使用)
  const [urlPageBodies, setUrlPageBodies] = useState<
    Record<MessageAudience, string>
  >({ customer: "", cast: "", staff: "", driver: "" });
  const [urlPageTitles, setUrlPageTitles] = useState<
    Record<MessageAudience, string>
  >({ customer: "", cast: "", staff: "", driver: "" });
  const [methods, setMethods] = useState<Record<MessageAudience, SendMethod>>({
    customer: "mail",
    cast: "mail",
    staff: "line",
    driver: "line",
  });
  const [sending, setSending] = useState<MessageAudience | null>(null);
  const [lastSent, setLastSent] = useState<Record<MessageAudience, string | null>>(
    { customer: null, cast: null, staff: null, driver: null }
  );
  const [copyTooltip, setCopyTooltip] = useState("コピー");

  const fetchTemplates = useCallback(async () => {
    if (!companyId) return;
    try {
      const list = await MessageTemplateApi.list(companyId);
      setTemplates(list);
      const next: Record<MessageAudience, number | null> = {
        customer: null,
        cast: null,
        staff: null,
        driver: null,
      };
      const nextBodies: Record<MessageAudience, string> = {
        customer: "",
        cast: "",
        staff: "",
        driver: "",
      };
      const nextSubjects: Record<MessageAudience, string> = {
        customer: "",
        cast: "",
        staff: "",
        driver: "",
      };
      AUDIENCE_TABS.forEach((aud) => {
        const def =
          list.find((t) => t.audience === aud && t.isDefault) ??
          list.find((t) => t.audience === aud);
        if (def) {
          next[aud] = def.messageTemplateId;
          nextBodies[aud] = def.body;
          nextSubjects[aud] = def.subject ?? "";
        }
      });
      setSelectedTemplateIds(next);
      setBodies(nextBodies);
      setSubjects(nextSubjects);

      // visit Phase 3: URL 先ページ本文 / タイトルも初期化
      const nextUrlPageBodies: Record<MessageAudience, string> = {
        customer: "",
        cast: "",
        staff: "",
        driver: "",
      };
      const nextUrlPageTitles: Record<MessageAudience, string> = {
        customer: "",
        cast: "",
        staff: "",
        driver: "",
      };

      // orderId あれば全 audience の既定をプレースホルダ展開済に置き換え
      if (effectiveOrderId) {
        for (const aud of AUDIENCE_TABS) {
          const tid = next[aud];
          if (!tid) continue;
          try {
            const rendered = await OrderMessageApi.preview(
              companyId,
              effectiveOrderId,
              { templateId: tid }
            );
            nextBodies[aud] = rendered.body;
            nextSubjects[aud] = rendered.subject;
            nextUrlPageBodies[aud] = rendered.urlPageBody;
            nextUrlPageTitles[aud] = rendered.urlPageTitle;
          } catch (e) {
            console.error(`preview 失敗 (${aud})`, e);
          }
        }
        setBodies({ ...nextBodies });
        setSubjects({ ...nextSubjects });
      }
      setUrlPageBodies(nextUrlPageBodies);
      setUrlPageTitles(nextUrlPageTitles);
    } catch (e) {
      console.error("テンプレ取得失敗", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, effectiveOrderId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = useMemo(
    () => templates.filter((t) => t.audience === tab),
    [templates, tab]
  );

  const handleTemplateChange = async (templateId: number) => {
    const t = templates.find((x) => x.messageTemplateId === templateId);
    if (!t) return;
    setSelectedTemplateIds({ ...selectedTemplateIds, [tab]: templateId });

    if (effectiveOrderId) {
      try {
        const rendered = await OrderMessageApi.preview(
          companyId,
          effectiveOrderId,
          { templateId }
        );
        setBodies({ ...bodies, [tab]: rendered.body });
        setSubjects({ ...subjects, [tab]: rendered.subject });
        setUrlPageBodies({ ...urlPageBodies, [tab]: rendered.urlPageBody });
        setUrlPageTitles({ ...urlPageTitles, [tab]: rendered.urlPageTitle });
        return;
      } catch (e) {
        console.error("preview 失敗、 素のテンプレを表示", e);
      }
    }
    // orderId が無い / preview 失敗 → 素のテンプレ (プレースホルダ未展開)
    setBodies({ ...bodies, [tab]: t.body });
    setSubjects({ ...subjects, [tab]: t.subject ?? "" });
    setUrlPageBodies({ ...urlPageBodies, [tab]: t.urlPageBody ?? "" });
    setUrlPageTitles({ ...urlPageTitles, [tab]: t.urlPageTitle ?? "" });
  };

  const handleCopy = async () => {
    const text = bodies[tab];
    try {
      await navigator.clipboard.writeText(text);
      setCopyTooltip("コピーしました");
      setTimeout(() => setCopyTooltip("コピー"), 2000);
    } catch (e) {
      console.error("コピー失敗", e);
    }
  };

  // #477: 外部 (DriverDispatchSendDialog の「保存して送信」 ボタン) から send を imperative に呼べるよう expose
  useImperativeHandle(ref, () => ({
    send: () => handleSend(),
  }));

  const handleSend = async () => {
    let orderIdToUse = effectiveOrderId;
    if (!orderIdToUse) {
      if (!onRequestSaveBeforeSend) {
        window.alert("受注を保存してから送信してください。");
        return;
      }
      try {
        orderIdToUse = await onRequestSaveBeforeSend();
      } catch {
        window.alert("保存に失敗したため送信できません。");
        return;
      }
      if (!orderIdToUse) return;
    }
    const body = bodies[tab];
    if (!body || body.trim().length === 0) {
      window.alert("本文が空です。");
      return;
    }
    // visit Phase 3: {URL_PAGE} 整合性チェック (Q2 d 案、 トグル不整合時 送信不可)
    const urlPageBody = urlPageBodies[tab];
    const hasUrlPagePlaceholder = body.includes("{URL_PAGE}");
    const hasUrlPageContent = urlPageBody && urlPageBody.trim().length > 0;
    if (hasUrlPagePlaceholder && !hasUrlPageContent) {
      window.alert(
        "本文に {URL_PAGE} がありますが、 URL 先ページ本文が空です。 URL 先ページ本文を入力するか、 本文の {URL_PAGE} を削除してください。"
      );
      return;
    }
    if (!hasUrlPagePlaceholder && hasUrlPageContent) {
      window.alert(
        "URL 先ページ本文が設定されていますが、 本文に {URL_PAGE} がありません。 本文の URL を表示したい位置に {URL_PAGE} を挿入してください。"
      );
      return;
    }
    const method = methods[tab];
    const audienceLabel = AUDIENCE_LABELS[tab];
    const methodLabel = METHODS_BY_AUDIENCE[tab].find((m) => m.value === method)
      ?.label;
    // #473: driver は送信先 (送り/迎え/両方) を確認文に表示。 両方かつ SMS は 2 通課金を警告
    const driverTargetLabel =
      tab === "driver"
        ? driverTarget === "both"
          ? " (送り・迎え両方)"
          : driverTarget === "return"
          ? " (迎えドライバー)"
          : " (送りドライバー)"
        : "";
    const bothNote =
      tab === "driver" && driverTarget === "both" && method === "sms"
        ? "\n※ 両方送信のため SMS 2 通分が課金されます。"
        : "";
    if (
      !window.confirm(
        `${audienceLabel}${driverTargetLabel}に「${methodLabel}」で送信します。 よろしいですか?${bothNote}`
      )
    )
      return;

    setSending(tab);
    try {
      const result = await OrderMessageApi.send(companyId, orderIdToUse, {
        audience: tab,
        sendMethod: method,
        customSubject: subjects[tab].trim() || undefined,
        customBody: body,
        customUrlPageBody: hasUrlPageContent ? urlPageBody : undefined,
        customUrlPageTitle: urlPageTitles[tab].trim() || undefined,
        // #473: driver のときのみ送信先 (送り/迎え/両方) を渡す
        driverTarget: tab === "driver" ? driverTarget : undefined,
      });
      setLastSent({ ...lastSent, [tab]: result.sentAt });
      const balanceMsg =
        result.smsBalance != null
          ? ` (SMS 残高: ¥${result.smsBalance.toLocaleString()})`
          : "";
      window.alert(`送信完了${balanceMsg}`);
      // #477: 送信成功時に parent で order refetch (配車画面の色を即時更新)
      if (onSent) onSent();
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? "送信に失敗しました";
      window.alert(msg);
    } finally {
      setSending(null);
    }
  };

  return (
    <Paper variant="outlined" style={{ padding: 12, marginTop: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">メッセージ送信</Typography>
        {lastSent[tab] ? (
          <Typography variant="caption" color="textSecondary">
            最終送信: {new Date(lastSent[tab]!).toLocaleString("ja-JP")}
          </Typography>
        ) : null}
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as MessageAudience)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        {AUDIENCE_TABS.map((aud) => (
          <Tab key={aud} value={aud} label={AUDIENCE_LABELS[aud]} />
        ))}
      </Tabs>

      <Box mt={2} display="flex" flexDirection="column" style={{ gap: 8 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>テンプレート</InputLabel>
          <Select
            value={selectedTemplateIds[tab] ?? ""}
            onChange={(e) => handleTemplateChange(Number(e.target.value))}
            label="テンプレート"
          >
            {filteredTemplates.length === 0 ? (
              <MenuItem value="" disabled>
                テンプレートがありません
              </MenuItem>
            ) : (
              filteredTemplates.map((t) => (
                <MenuItem key={t.messageTemplateId} value={t.messageTemplateId}>
                  {t.isDefault ? (
                    <StarIcon
                      style={{ color: "#fbc02d", fontSize: 16, marginRight: 4 }}
                    />
                  ) : null}
                  {t.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <TextField
          label="件名 (メールのみ)"
          value={subjects[tab]}
          onChange={(e) =>
            setSubjects({ ...subjects, [tab]: e.target.value })
          }
          size="small"
          fullWidth
        />

        <TextField
          label={
            urlPageBodies[tab]
              ? "SMS 本文 (70 字以内推奨、 {URL_PAGE} で URL 挿入)"
              : "本文"
          }
          value={bodies[tab]}
          onChange={(e) => setBodies({ ...bodies, [tab]: e.target.value })}
          multiline
          rows={compactRows ?? (urlPageBodies[tab] ? 5 : 18)}
          fullWidth
          variant="outlined"
        />

        {/* visit Phase 3: SMS 文字数カウンター (customer / sms method のみ表示) */}
        {tab === "customer" && methods[tab] === "sms" && (
          (() => {
            const hasUrlPage = !!urlPageBodies[tab];
            const length = calcSmsLength(bodies[tab], hasUrlPage);
            const segments = Math.max(
              1,
              Math.ceil(length / SMS_CHARS_PER_SEGMENT)
            );
            const isOverSoftLimit = hasUrlPage && length > URL_SMS_SOFT_LIMIT;
            return (
              <Box
                style={{
                  padding: "6px 10px",
                  backgroundColor: isOverSoftLimit ? "#fff3e0" : "#f5f5f5",
                  borderRadius: 4,
                  fontSize: 12,
                  border: isOverSoftLimit ? "1px solid #ffb74d" : "none",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <span>
                  📱 本文長: <strong>{length}</strong>
                  {hasUrlPage ? ` / ${URL_SMS_SOFT_LIMIT}` : ""} 文字
                  {hasUrlPage && " (短縮 URL 含む)"}
                </span>
                <span>
                  送信通数: <strong>{segments}</strong> 通 (70 文字単位)
                </span>
                {isOverSoftLimit && (
                  <span
                    style={{
                      color: "#e65100",
                      fontWeight: "bold",
                      flexBasis: "100%",
                    }}
                  >
                    ⚠️ {URL_SMS_SOFT_LIMIT} 文字を超えています。 SMS が 2 通分課金になる可能性あり
                  </span>
                )}
              </Box>
            );
          })()
        )}

        {urlPageBodies[tab] && (
          <Box
            style={{
              border: "1px solid #b3e5fc",
              borderRadius: 4,
              padding: 10,
              backgroundColor: "#f5fafc",
            }}
          >
            <Typography
              variant="caption"
              style={{
                fontWeight: "bold",
                color: "#0277bd",
                display: "block",
                marginBottom: 6,
              }}
            >
              📎 URL 先ページ本文 (受注ごとに編集可、 SMS では本文の {`{URL_PAGE}`} が短縮 URL に置換されます)
            </Typography>
            <TextField
              label="ページタイトル (任意)"
              value={urlPageTitles[tab]}
              onChange={(e) =>
                setUrlPageTitles({ ...urlPageTitles, [tab]: e.target.value })
              }
              size="small"
              fullWidth
              style={{ marginBottom: 6 }}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="URL 先ページ本文"
              value={urlPageBodies[tab]}
              onChange={(e) =>
                setUrlPageBodies({
                  ...urlPageBodies,
                  [tab]: e.target.value,
                })
              }
              multiline
              rows={12}
              fullWidth
              variant="outlined"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: 4, display: "block" }}
            >
              ※ 送信 48 時間後に自動削除されます (個人情報保護)
            </Typography>
          </Box>
        )}

        {tab === "driver" && (
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography variant="caption" color="textSecondary">
              送信先:
            </Typography>
            <RadioGroup
              row
              value={driverTarget}
              onChange={(e) =>
                setDriverTarget(
                  e.target.value as "outward" | "return" | "both"
                )
              }
            >
              <FormControlLabel
                value="outward"
                control={<Radio size="small" color="primary" />}
                label="送り"
              />
              <FormControlLabel
                value="return"
                control={<Radio size="small" color="primary" />}
                label="迎え"
              />
              <FormControlLabel
                value="both"
                control={<Radio size="small" color="primary" />}
                label="両方"
              />
            </RadioGroup>
          </Box>
        )}

        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Typography variant="caption" color="textSecondary">
            送信方法:
          </Typography>
          <RadioGroup
            row
            value={methods[tab]}
            onChange={(e) =>
              setMethods({
                ...methods,
                [tab]: e.target.value as SendMethod,
              })
            }
          >
            {METHODS_BY_AUDIENCE[tab].map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                control={<Radio size="small" color="primary" />}
                label={m.label}
              />
            ))}
          </RadioGroup>

          <Box flex={1} />

          <Tooltip title={copyTooltip}>
            <IconButton size="small" onClick={handleCopy}>
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* #477: 統合 Dialog (DriverDispatchSendDialog) から外部 footer で送信する場合は内部ボタンを隠す */}
          {!hideSendButton && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={
                sending === tab ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              disabled={sending !== null || !effectiveOrderId}
              onClick={handleSend}
            >
              今送信
            </Button>
          )}
        </Box>

        {!effectiveOrderId ? (
          <Chip
            label="受注を保存してから送信可能です"
            size="small"
            variant="outlined"
            style={{ alignSelf: "flex-start" }}
          />
        ) : null}
      </Box>
    </Paper>
  );
});

OrderMessagePanel.displayName = "OrderMessagePanel";

export default OrderMessagePanel;
